import { useState } from 'react';

import type { CheckboxButtonGroupOption } from '@capsule/consumer-design-system';
import { ScreenReaderOnly, CheckboxButtonGroupVariant } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { FormikCheckboxButtonGroup } from 'src/app/components/Form/FormikCheckboxButtonGroup/FormikCheckboxButtonGroup';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { oxfordCommaFormatter, persistedFormValue } from 'src/app/components/Pages/EscriptOnboarding/utils';
import { usePatientList, usePatientUpdate } from 'src/app/hooks/data/patient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { newOnboardingStep } from 'src/app/router/paths';
import { ESCRIPT_ONBOARDING_OTHER_MEDS_FORM } from 'src/app/storage/localStorageKeys';
import { getPrimaryPatientFromList } from 'src/app/utils/data/patient';

export const antiobioticsOption = 'Antiviral/antibiotics' as const;

export const otherMedicationsOptions = [
  'Asthma',
  antiobioticsOption,
  'Birth control',
  'Blood pressure',
  'Cholesterol',
  'Dermatology',
  'Diabetes',
  'Digestive',
  'Mental health',
  'Seasonal allergy',
  'Sexual health',
  'Thyroid',
  'Vitamin supplements',
  'Others',
] as const;

const otherMedicationsFormOptions: Pick<CheckboxButtonGroupOption, 'label' | 'value'>[] = otherMedicationsOptions.map(
  medication => ({
    label: medication,
    value: medication,
  })
);

export type OtherMedsFormValues = {
  [key in typeof otherMedicationsOptions[number]]: boolean;
} & { other_medication_types: string };

export const otherMedicationFormInitialFormValues: OtherMedsFormValues = {
  ...Object.assign({}, ...otherMedicationsOptions.map(medication => ({ [medication]: false }))),
  other_medication_types: '',
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s4};
`;

/** We use empty spaces to create a truthy value that is renderless for errors in this form. */
const EMPTY_SPACE = ' ';

const FORM_ID = 'otherMedsForm';

export const EscriptOnboardingOtherMedsYes = () => {
  useSegmentEvent('viewCheckoutOtherMedicationsDetails', { shouldTrackOnMount: true });
  const analyticsDispatchSaveOtherMedicationsDetails = useSegmentEvent('saveCheckoutOtherMedicationsDetails', {
    shouldTrackOnMount: false,
  });
  const history = useHistory();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { initialValue, persistValue } = persistedFormValue(
    ESCRIPT_ONBOARDING_OTHER_MEDS_FORM,
    otherMedicationFormInitialFormValues
  );

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';
  const { data: patients } = usePatientList(parseInt(accountId), Boolean(accountId));

  const primaryPatient = patients ? getPrimaryPatientFromList(patients) : null;

  const { mutateAsync: mutatePatientAsync } = usePatientUpdate(
    primaryPatient ? parseInt(primaryPatient.id) : -1,
    accountId ? parseInt(accountId) : -1
  );

  return (
    <Formik
      initialValues={initialValue}
      validateOnBlur={hasSubmitted}
      validateOnChange={hasSubmitted}
      validate={formValue => {
        // user must type something in the "other_medication_types" TextField if they select "Others"
        if (formValue.Others && formValue.other_medication_types === '') {
          return { other_medication_types: 'Please type your other medications here' };
        }

        const hasTruthyValue = Object.values(formValue).some(val => !!val === true);
        // no errors if some value exists!
        if (hasTruthyValue) {
          return {};
        }

        const allFieldsErrored = Object.assign(
          {},
          ...Object.keys(otherMedicationFormInitialFormValues).map(keyName => ({ [keyName]: EMPTY_SPACE }))
        );

        /**
         * This error object contains an error value for a field called `form`. The
         * individual fields will be in an error state without a helpful error message,
         * but `errors.form` should be leveraged somewhere else to properly announce
         * relevant form errors.
         */
        return {
          ...allFieldsErrored,
          form: (
            <>
              Please make at least one selection
              <ScreenReaderOnly>
                . If you are not taking other medication, and mistakenly navigated to this page, you can also go back
                and avoid this step.
              </ScreenReaderOnly>
            </>
          ),
        };
      }}
      onSubmit={async formValue => {
        const { other_medication_types: textInputValue, ...optionValues } = formValue;
        const selectedMedTypes = otherMedicationsOptions.filter(med => !!optionValues[med]);
        const formattedSubmission = oxfordCommaFormatter(
          textInputValue && selectedMedTypes.includes('Others')
            ? // if user has selected "Others" and specified a value,
              // we won't include "Others" in the submission. If they
              // don't write anything on the other_medication_types
              // box, we'll keep "Others" in the submission
              [...selectedMedTypes.filter(medType => medType !== 'Others'), textInputValue]
            : selectedMedTypes
        );

        persistValue(formValue);
        await mutatePatientAsync({ otherMedications: formattedSubmission });
        analyticsDispatchSaveOtherMedicationsDetails({
          medications: formattedSubmission,
          num_medications_shown: null,
          perc_medications_selected: null,
        });
        history.push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.any_doctor_any_med }));
      }}
    >
      {({ errors, isSubmitting, values }) => (
        <SimpleFeatureLayout
          keyName={EscriptOnboardingStepName.other_meds_yes}
          title={`Which other types of meds are you taking right now?`}
          description={`Select any that apply.`}
          body={{
            content: (
              <StyledForm as={Form} noValidate id={FORM_ID}>
                <FormikCheckboxButtonGroup
                  label={{ content: 'Mark all relevant options.', isHidden: true }}
                  options={otherMedicationsFormOptions}
                  variant={CheckboxButtonGroupVariant.MultiColumnHug}
                />
                {values.Others && (
                  <FormikTextField
                    name="other_medication_types"
                    label={{ content: `Other medication types (please list)`, isHidden: false }}
                    error={errors.other_medication_types}
                  />
                )}
              </StyledForm>
            ),
          }}
          cta1={{ children: 'Submit', isSubmitting, form: FORM_ID, onClick: () => setHasSubmitted(true) }}
          // @ts-expect-error - There _is_ a type error here, but we're ignoring it because it's the simplest way to express top-level errors.
          error={errors?.form}
        />
      )}
    </Formik>
  );
};
